import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, CheckCircle2, XCircle, Hourglass, Users, FolderKanban } from "lucide-react";

const STATUS_STYLES = {
  draft: "bg-neutral-100 text-neutral-700",
  pending: "bg-[#FEF7E0] text-[#B98900]",
  approved: "bg-[#E6F4EA] text-[#008060]",
  rejected: "bg-[#FCE8E6] text-[#D82C0D]",
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api
      .get("/stats/admin")
      .then((r) => setStats(r.data))
      .catch((e) => toast.error(formatApiError(e)));
    api
      .get("/timesheets/all")
      .then((r) => setRecent(r.data.slice(0, 8)))
      .catch(() => {});
  }, []);

  return (
    <div className="p-6 md:p-10 max-w-[1400px]">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Overview</div>
        <h1 className="font-display font-black text-3xl md:text-5xl leading-none">
          Command center.
        </h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Tile
          label="Pending"
          value={stats?.pending ?? "—"}
          Icon={Hourglass}
          tone="pending"
          testid="stat-pending"
        />
        <Tile
          label="Approved"
          value={stats?.approved ?? "—"}
          Icon={CheckCircle2}
          tone="approve"
          testid="stat-approved"
        />
        <Tile
          label="Rejected"
          value={stats?.rejected ?? "—"}
          Icon={XCircle}
          tone="reject"
          testid="stat-rejected"
        />
        <Tile
          label="Approved hours"
          value={stats?.total_approved_hours ?? "—"}
          Icon={Clock}
          tone="neutral"
          testid="stat-hours"
        />
        <Tile label="Billable hours" value={stats?.total_billable_hours ?? "—"} Icon={Clock} tone="neutral" testid="stat-billable" />
        <Tile label="Staff" value={stats?.staff ?? "—"} Icon={Users} tone="neutral" testid="stat-staff" />
        <Tile label="Projects" value={stats?.projects ?? "—"} Icon={FolderKanban} tone="neutral" testid="stat-projects" />
        <Link
          to="/admin/approvals"
          className="bg-[#111111] text-white rounded-md p-5 flex items-center justify-between hover:bg-[#0033CC] transition-colors"
          data-testid="review-queue-cta"
        >
          <div>
            <div className="text-[11px] uppercase tracking-widest text-white/60">Action</div>
            <div className="font-display font-black text-lg mt-1">Review queue</div>
          </div>
          <ArrowRight size={20} />
        </Link>
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E5E5E5] flex justify-between items-center">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-neutral-500">Latest</div>
            <div className="font-display font-black text-lg mt-1">Recent activity</div>
          </div>
          <Link
            to="/admin/reports"
            className="text-sm text-[#0033CC] hover:underline"
            data-testid="view-all-link"
          >
            View all →
          </Link>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[#F3F3F2]">
            <tr className="text-left text-xs uppercase tracking-wider text-neutral-500">
              <th className="px-4 py-3">Staff</th>
              <th className="px-4 py-3">Week</th>
              <th className="px-4 py-3 text-right">Hours</th>
              <th className="px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {recent.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-neutral-500">
                  No timesheets yet.
                </td>
              </tr>
            ) : (
              recent.map((r) => (
                <tr key={r.id} className="border-t border-[#E5E5E5]">
                  <td className="px-4 py-3 font-medium">{r.user_name}</td>
                  <td className="px-4 py-3 font-mono-num">{r.week_start}</td>
                  <td className="px-4 py-3 text-right font-mono-num">{r.total_hours}</td>
                  <td className="px-4 py-3">
                    <span className={`status-pill ${STATUS_STYLES[r.status]}`}>{r.status}</span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Tile({ label, value, Icon, tone, testid }) {
  const tones = {
    pending: "bg-[#FEF7E0] text-[#B98900]",
    approve: "bg-[#E6F4EA] text-[#008060]",
    reject: "bg-[#FCE8E6] text-[#D82C0D]",
    neutral: "bg-neutral-100 text-neutral-700",
  };
  return (
    <div className="bg-white border border-[#E5E5E5] p-5 rounded-md" data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <div className="text-[11px] uppercase tracking-widest text-neutral-500">{label}</div>
        <div className={`w-8 h-8 flex items-center justify-center rounded-sm ${tones[tone]}`}>
          <Icon size={16} />
        </div>
      </div>
      <div className="font-display font-black text-3xl font-mono-num">{value}</div>
    </div>
  );
}
